import { Point } from '@printu/geom';
import { generateFancyArrowPoints } from 'app/core/graphics/Arrow';
import { getCenterParameters } from 'app/core/rendering/arc';
import { noop } from 'app/utils/function';
export class Compiler {
    constructor() {
        Object.defineProperty(this, "actions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
    }
    getRunner() {
        const actions = this.actions.concat();
        this.actions.splice(0, actions.length);
        return (ctx) => {
            ctx.beginPath();
            actions.forEach(action => action(ctx));
        };
    }
    push(fnc, args) {
        this.actions.push((ctx) => ctx[fnc](...args));
    }
}
export const compilePath = (pathData, usePathAPI = false) => {
    if (!pathData) {
        return noop;
    }
    if (usePathAPI) {
        if ('Path2d' in global) {
            try {
                const path = new Path2D(pathData);
                return (ctx) => {
                    if (ctx.fillStyle) {
                        ctx.fill(path);
                    }
                    if (ctx.lineWidth > 0) {
                        ctx.stroke(path);
                    }
                };
            }
            catch (_) {
                /** noop */
            }
        }
    }
    const parts = pathData && pathData.match(/[mlhvcsqtaz][^mlhvcsqtaz]*/gi);
    if (parts.length === 0) {
        return noop;
    }
    let previous = '';
    let current = new Point();
    let start = new Point();
    let control = new Point();
    let relative;
    let coords;
    const compiler = new Compiler();
    const getValue = (index) => +coords[index];
    const getCoord = (index, coord) => {
        let val = getValue(index);
        if (relative) {
            val += current[coord];
        }
        return val;
    };
    const getPoint = (index) => new Point(getCoord(index, 'x'), getCoord(index + 1, 'y'));
    for (let i = 0, maxIndex = parts && parts.length; i < maxIndex; i += 1) {
        const part = parts[i];
        const command = part[0];
        const lower = command.toLowerCase();
        coords = part.match(/[+-]?(?:\d*\.\d+|\d+\.?)(?:[eE][+-]?\d+)?/g);
        const length = coords && coords.length;
        relative = command === lower;
        if (previous === 'z' && !/[mz]/.test(lower)) {
            compiler.push('moveTo', [current.x, current.y]);
        }
        switch (lower) {
            case 'm':
            case 'l': {
                let move = lower === 'm';
                for (let j = 0; j < length; j += 2) {
                    current = getPoint(j);
                    if (move) {
                        compiler.push('moveTo', [current.x, current.y]);
                    }
                    else {
                        compiler.push('lineTo', [current.x, current.y]);
                    }
                    if (move) {
                        start = current;
                        move = false;
                    }
                }
                control = current;
                break;
            }
            case 'h':
            case 'v': {
                const coord = lower === 'h' ? 'x' : 'y';
                current = current.clone();
                for (let j = 0; j < length; j += 1) {
                    current[coord] = getCoord(j, coord);
                    compiler.push('lineTo', [current.x, current.y]);
                }
                control = current;
                break;
            }
            case 'c':
                for (let j = 0; j < length; j += 6) {
                    const cp0 = getPoint(j);
                    control = getPoint(j + 2);
                    current = getPoint(j + 4);
                    compiler.push('bezierCurveTo', [
                        cp0.x,
                        cp0.y,
                        control.x,
                        control.y,
                        current.x,
                        current.y,
                    ]);
                }
                break;
            case 'a':
                {
                    const rx = getValue(0);
                    const ry = getValue(1);
                    const phi = getValue(2);
                    const fa = getValue(3);
                    const fs = getValue(4);
                    const point = getPoint(5);
                    const centerParams = getCenterParameters(current.x, current.y, point.x, point.y, fa, fs, rx, ry, phi);
                    compiler.push('arc', [
                        centerParams.cx,
                        centerParams.cy,
                        rx,
                        centerParams.theta,
                        centerParams.phi,
                    ]);
                    current = point;
                }
                break;
            case 's':
                for (let j = 0; j < length; j += 4) {
                    const cp0 = /[cs]/.test(previous)
                        ? current.multiply(2).subtract(control)
                        : current;
                    control = getPoint(j);
                    current = getPoint(j + 2);
                    compiler.push('bezierCurveTo', [
                        cp0.x,
                        cp0.y,
                        control.x,
                        control.y,
                        current.x,
                        current.y,
                    ]);
                    previous = lower;
                }
                break;
            case 'q':
                for (let j = 0; j < length; j += 4) {
                    control = getPoint(j);
                    current = getPoint(j + 2);
                    compiler.push('quadraticCurveTo', [
                        control.x,
                        control.y,
                        current.x,
                        current.y,
                    ]);
                }
                break;
            case 't':
                for (let j = 0; j < length; j += 2) {
                    control = /[qt]/.test(previous)
                        ? current.multiply(2).subtract(control)
                        : current;
                    current = getPoint(j);
                    compiler.push('quadraticCurveTo', [
                        control.x,
                        control.y,
                        current.x,
                        current.y,
                    ]);
                    previous = lower;
                }
                break;
            case 'z':
                // ctx.closePath();
                compiler.push('closePath', []);
                current = start;
                break;
            default:
                break;
        }
        previous = lower;
    }
    return compiler.getRunner();
};
export const compileFancyArrowPath = (p1, p2, arrowWidth) => {
    const points = generateFancyArrowPoints(p1, p2, arrowWidth);
    if (!points.length) {
        return null;
    }
    const compiler = new Compiler();
    compiler.push('beginPath', []);
    points
        .map(point => [point.x, point.y])
        .forEach((coords, index) => {
        compiler.push(index === 0 ? 'moveTo' : 'lineTo', coords);
    });
    compiler.push('closePath', []);
    return compiler.getRunner();
};
export const compileShapePath = (shape) => {
    const compiler = new Compiler();
    compiler.push('beginPath', []);
    shape.forEach(item => {
        switch (item.type) {
            case 'line':
                compiler.push('moveTo', [item.start.x, item.start.y]);
                compiler.push('lineTo', [item.end.x, item.end.y]);
                break;
            case 'polyline':
                {
                    const [firstPoint, ...points] = item.points;
                    compiler.push('moveTo', [firstPoint.x, firstPoint.y]);
                    points.forEach(point => compiler.push('lineTo', [point.x, point.y]));
                }
                break;
            default:
                break;
        }
    });
    return compiler.getRunner();
};
